import { useEffect, useMemo, useState } from 'react';
import { AlertTriangle, Download, FileSpreadsheet, RefreshCw, Wallet } from 'lucide-react';
import API from '../services/api';

const monthNames = [
  'Januar', 'Februar', 'März', 'April', 'Mai', 'Juni',
  'Juli', 'August', 'September', 'Oktober', 'November', 'Dezember'
];

function money(value) {
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(value || 0));
}

function AccountingWorkbookPanel() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [summary, setSummary] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [message, setMessage] = useState('');

  const load = async () => {
    setLoading(true);
    setMessage('');
    try {
      const [{ data: summaryData }, { data: issueData }] = await Promise.all([
        API.get('/accounting/summary', { params: { year } }),
        API.get('/accounting/issues', { params: { year } })
      ]);
      setSummary(summaryData);
      setIssues(Array.isArray(issueData?.rows) ? issueData.rows : []);
    } catch (err) {
      console.error(err);
      setMessage('Buchhaltungsdaten konnten nicht geladen werden: ' + (err.response?.data?.message || err.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [year]);

  const totals = summary?.totals || {};
  const years = useMemo(() => {
    const current = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => current - i);
  }, []);

  const download = async () => {
    try {
      const response = await API.get('/accounting/export', { params: { year }, responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Stripe-Zahlungen-${year}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setMessage('Excel konnte nicht erstellt werden: ' + (err.response?.data?.message || err.message));
    }
  };

  const reconcile = async () => {
    setSyncing(true);
    setMessage('');
    try {
      const { data } = await API.post('/accounting/reconcile-stripe', { year });
      setMessage(`Stripe-Abgleich abgeschlossen: ${data.updated || 0} Zahlung(en) aktualisiert${data.errors?.length ? `, ${data.errors.length} Fehler` : ''}.`);
      await load();
    } catch (err) {
      setMessage(err.response?.data?.message || 'Stripe-Abgleich konnte nicht ausgeführt werden.');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="page">
      <div style={{ padding: 24, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
        <div>
          <h1 style={{ fontSize: 28, marginBottom: 8 }}>Buchhaltung</h1>
          <div style={{ color: 'var(--text2)', fontSize: 14 }}>
            Stripe-Zahlungen nach Zahlungsmonat · fortlaufende Arbeitsmappe für den Steuerberater
          </div>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <select value={year} onChange={(e) => setYear(Number(e.target.value))} style={{ padding: '9px 12px', border: '1px solid var(--border)', borderRadius: 8, background: 'var(--bg)', color: 'var(--text1)' }}>
            {years.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn btn-ghost" onClick={reconcile} disabled={syncing}>
            <RefreshCw size={17} style={{ marginRight: 6 }} />
            {syncing ? 'Stripe wird abgeglichen…' : 'Stripe-Daten abgleichen'}
          </button>
          <button className="btn" onClick={download} style={{ background: 'var(--accent)', color: 'white' }}>
            <Download size={17} style={{ marginRight: 6 }} /> Excel {year} herunterladen
          </button>
        </div>
      </div>

      <div style={{ padding: 24 }}>
        {message && <div className="card" style={{ padding: 14, marginBottom: 16 }}>{message}</div>}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 14, marginBottom: 22 }}>
          <div className="card" style={{ padding: 16 }}><FileSpreadsheet size={18} /><div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 8 }}>ZAHLUNGSGRUPPEN</div><div style={{ fontSize: 28, fontWeight: 700 }}>{totals.payment_groups || 0}</div></div>
          <div className="card" style={{ padding: 16 }}><Wallet size={18} /><div style={{ color: 'var(--text2)', fontSize: 12, marginTop: 8 }}>GUTSCHRIFTEN</div><div style={{ fontSize: 28, fontWeight: 700 }}>{money(totals.gross)}</div></div>
          <div className="card" style={{ padding: 16 }}><div style={{ color: 'var(--text2)', fontSize: 12 }}>STRIPE-GEBÜHREN</div><div style={{ fontSize: 28, fontWeight: 700 }}>{money(totals.fee)}</div></div>
          <div className="card" style={{ padding: 16 }}><div style={{ color: 'var(--text2)', fontSize: 12 }}>NETTO</div><div style={{ fontSize: 28, fontWeight: 700 }}>{money(totals.net)}</div></div>
        </div>

        {issues.length > 0 && (
          <div className="card" style={{ padding: 16, marginBottom: 22, borderColor: 'rgba(245,158,11,.5)' }}>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', fontWeight: 700, marginBottom: 8 }}>
              <AlertTriangle size={18} /> {issues.length} Zahlung(en) benötigen Stripe-Abgleich
            </div>
            <div style={{ color: 'var(--text2)', fontSize: 13 }}>
              Historische Excel-Werte bleiben erhalten. Sobald Stripe verbunden ist, können fehlende oder rechnerisch auffällige Werte automatisch geprüft werden.
            </div>
          </div>
        )}

        <div className="card" style={{ overflowX: 'auto' }}>
          <table className="table" style={{ width: '100%' }}>
            <thead><tr><th>Monat</th><th>Zahlungen</th><th>Bestellzeilen</th><th style={{ textAlign: 'right' }}>Gutschriften</th><th style={{ textAlign: 'right' }}>Gebühren</th><th style={{ textAlign: 'right' }}>Netto</th><th>Prüfen</th></tr></thead>
            <tbody>
              {(summary?.months || []).map((row) => (
                <tr key={row.month}>
                  <td style={{ fontWeight: 700 }}>{monthNames[row.month - 1]}</td>
                  <td>{row.payment_groups}</td>
                  <td>{row.order_rows}</td>
                  <td style={{ textAlign: 'right' }}>{money(row.gross)}</td>
                  <td style={{ textAlign: 'right' }}>{money(row.fee)}</td>
                  <td style={{ textAlign: 'right' }}>{money(row.net)}</td>
                  <td>{row.issues ? `⚠ ${row.issues}` : '✓'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {loading && <div style={{ padding: 18, color: 'var(--text2)' }}>Buchhaltungsdaten werden geladen…</div>}
        </div>
      </div>
    </div>
  );
}

export default AccountingWorkbookPanel;
